import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';

import { LoanFormat } from '../../../models/LoanFormat';
import { AgeCategory } from '../../../models/AgeCategory';
import { GlobalReactQueryOptions } from '../../config';
import {
    fetchLanguageLearningLanguages,
    FetchLanguagesResponse,
    fetchLoteLanguages
} from '../../api/languages/fetchLanguages';
import { UseQueryResult } from '../../api/query-types';

export enum LanguageScope {
    LANGUAGE_LEARNING = 'languageLearning',
    LOTE = 'lote'
}

type Language = {
    readonly name: string;
    readonly count: number;
};

type Options = {
    readonly scope: LanguageScope;
    readonly loanFormat: LoanFormat | undefined;
    readonly ageCategory?: AgeCategory;
    readonly shopLanguage?: string | undefined;
};

type UseLanguageLearningLanguages = UseQueryResult<'languages', ReadonlyArray<Language>>;

const fetchFuncMapping = {
    languageLearning: fetchLanguageLearningLanguages,
    lote: fetchLoteLanguages
};

const initialData: FetchLanguagesResponse = {
    buckets: []
};

export function useLanguages({ scope, loanFormat, ageCategory, shopLanguage }: Options): UseLanguageLearningLanguages {
    const params = { scope, loanFormat, ageCategory, shopLanguage };
    const { data, ...result } = useQuery(
        ['languages', params],
        () => {
            return fetchFuncMapping[scope](params);
        },
        {
            ...GlobalReactQueryOptions,
            initialData
        }
    );

    const languages = useMemo(
        () => (!data ? [] : data.buckets.map(bucketEntry => ({ name: bucketEntry.key, count: bucketEntry.docCount }))),
        [data]
    );

    return {
        ...result,
        languages
    };
}
